import { camelizeDeep } from '@vpr/restful';

import type { Viewer } from '@vimeo/viewer';

const bootstrapId = 'viewer-bootstrap';

/**
 * Logs an error to either datadog or the console, depending on the environment.
 */
const logError = (msg: string) => {
  if (process.env.NODE_ENV && typeof window !== 'undefined') {
    window.DD_RUM && window.DD_RUM.addError(msg);
  } else {
    console.error(msg);
  }
};

/**
 * Returns a Promise that resolves with the viewer object (or null, if you happen
 * to call `getViewerPromise` in SSG-executed code).
 *
 * We first check to see if the viewer is inlined in the DOM, which we do for
 * logged in users. If that's the case, the viewerPromise resolves immediately
 * with the viewer.
 *
 * If the viewer bootstrap isn't available, or is corrupt somehow, then we make a
 * request to fetch the viewer.
 */
export const getViewerPromise = (): Promise<Viewer | null> => {
  if (typeof window === 'undefined') {
    logError('Do not call getViewerPromise in code that executes during the SSG build.');
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let viewerBootstrap;
    const viewerScript = document.getElementById(bootstrapId);
    if (viewerScript) {
      try {
        viewerBootstrap = camelizeDeep(JSON.parse(viewerScript.innerText));

        // The viewerBootstrap isn't an object or if it's empty, something real
        // weird happened and we don't want to use it.
        if (typeof viewerBootstrap !== 'object' || Object.keys(viewerBootstrap).length === 0) {
          viewerBootstrap = null;
          logError('Viewer bootstrap in DOM, but empty');
        }
      } catch {
        logError('Viewer bootstrap failed to parse');
      }
    }

    // If the viewer was in the DOM, we're done 🎉
    if (viewerBootstrap) {
      resolve(viewerBootstrap);
    }
    // Otherwise, we need to fetch it.
    else {
      resolve(
        fetch('/_next/viewer')
          .then((res) => res.json())
          .then(camelizeDeep),
      );
    }
  });
};
